<template>
	<App title="Update Station">
		<div class="w-100 position-relative">
		<div class="update-layout">

			<!-- ── Hero ──────────────────────────────────────────────────── -->
			<div class="update-hero">
				<div class="update-avatar">
					<i class="fas fa-charging-station"></i>
				</div>
				<div class="update-hero-info">
					<h2 class="update-name">{{ station.title }}</h2>
					<span class="update-role-badge">{{ station.type || 'Station' }}</span>
				</div>
				<div class="update-hero-actions">
					<a :href="route('station.page.single', { station: station.slug })" class="btn btn-sm btn-outline-secondary">
						<i class="fas fa-arrow-left me-1"></i> Back
					</a>
				</div>
			</div>

			<!-- ── Form card ──────────────────────────────────────────────── -->
			<div class="update-card">
				<div class="update-card-header">
					<i class="fas fa-store me-2 text-muted"></i>
					<span>Station Details</span>
				</div>

				<Form ref="form" @submit="onSubmit">
					<div class="update-form-grid">

						<div class="update-field">
							<label for="title" class="update-label">Station Name</label>
							<InputField id="title" v-model="title" name="title" rules="required" label="Station Name" placeholder="Input station name" />
						</div>

						<div class="update-field">
							<label for="type" class="update-label">Station Type</label>
							<SelectField id="type" v-model="type" name="type" rules="required" label="Station Type" :options="type_options">
								<option v-for="t in types" :key="t.name" :value="t.value">{{ t.value }}</option>
							</SelectField>
						</div>

						<div class="update-field update-field--full">
							<label for="address" class="update-label">Address</label>
							<InputField id="address" v-model="address" name="address" rules="required" label="Address" placeholder="123 Main Street" />
						</div>

					</div>

					<!-- ── Account section ─────────────────────────────────── -->
					<div class="update-section-divider">
						<i class="fas fa-university me-2 text-muted"></i>
						<span>Account Information</span>
					</div>

					<div class="update-form-grid">

						<div class="update-field">
							<label for="bank" class="update-label">Bank Name</label>
							<SelectField id="bank" ref="bankField" class="bank-name-select" v-model="account.bank" name="bank" rules="required" label="Bank Name" :options="banks_options" />
						</div>

						<div class="update-field">
							<label for="number" class="update-label">Account Number</label>
							<InputField :disabled="account.bank === ''" id="number" v-model="account.number" name="number" rules="max:10|numeric" label="Account Number" placeholder="10-digit account number" @blur="handleAccountBlur" />
							<span class="update-hint update-hint--muted" v-if="validatingAccount">
								<i class="fas fa-spinner fa-spin me-1"></i> Validating account…
							</span>
							<span class="update-hint update-hint--error" v-if="invalid_acctno_msg && !validatingAccount">
								<i class="fas fa-exclamation-circle me-1"></i>{{ invalid_acctno_msg }}
							</span>
							<span class="update-hint update-hint--success" v-if="account.name && !validatingAccount">
								<i class="fas fa-check-circle me-1"></i>{{ account.name }}
							</span>
						</div>

					</div>

					<input type="hidden" id="index" name="index" v-model="account.index" />

					<!-- ── Actions ─────────────────────────────────────────── -->
					<div class="update-form-actions">
						<button type="button" class="btn btn-outline-danger" :disabled="loading || done" @click="confirmAction">
							<i class="fas fa-times me-1"></i> Cancel
						</button>
						<Button id="action" type="submit" :label="labels" :loading="loading" :done="done" class="btn btn-primary" :disabled="checkInput" />
					</div>
				</Form>
			</div>

		</div>

		<div class="mt-9 position-absolute top-0 end-0">
			<NotivueKeyboard v-slot="{ containersTabIndex }">
				<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
					<AppNotification :item="item" />
				</Notivue>
			</NotivueKeyboard>
		</div>
		</div>
	</App>
</template>

<script lang="ts">

import $ from 'jquery'
import banks from '@/json/banks.json' // Your JSON file
import { useConfirm } from '@/libs/useConfirm.ts'
import locations from '@/json/location.json' 
import { error } from '@/ts/app'
import Swal from 'sweetalert2'
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import PhoneField from '@/vue/components/PhoneField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import TextareaField from '@/vue/components/TextareaField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { Options as Select2Options } from 'select2'
import { Component, Prop, Ref, Vue, Watch, toNative } from 'vue-facing-decorator'
import { Notivue, Notification, push, materialTheme, NotificationProgress, NotivueKeyboard  } from 'notivue'
import AppNotification from '@/vue/components/AppNotification.vue'
import type Station from '@/models/Station'

@Component({
	components: {
		App,
		Form,
		Alert,
		InputField,
		PhoneField,
		Button,
		Block,
		SelectField,
		TextareaField,
		Notivue,
		Notification,
		NotificationProgress,
		AppNotification,
		NotivueKeyboard
	
	},
})
class Update extends Vue {
	message = ''
	confirm = useConfirm().confirm
	loading = false
	done = false
	//state = ''
	title = ''
	address = ''
	type =  ''
	cluster = ''

	manager= ''
	//zone = ''
	validatingAccount = false
	account = {
		number: '',
		index: '',
		bank: '',
		name: '',
		
	}
	invalid_acctno_msg = ''
	materialTheme = materialTheme
	@Prop({ type: Array })
	readonly types!: Array<{ name: string; value: string }>

	@Ref('form')
	readonly form!: InstanceType<typeof Form>
		@Ref('bankField') bankField!: any
	
	@Prop({ type: Object })
	readonly station!: Station

/*
	@Ref('alert')
	readonly alert!: InstanceType<typeof Alert>

	@Watch('message')
	onMessageChanged(value: string) {
		if (value.length > 0) {
			this.alert.show()
		} else {
			this.alert.hide()
		}
	}
*/

	get banks_options(): Select2Options {
		return {
			ajax: null,
			placeholder: 'Select Bank',
			minimumResultsForSearch: Infinity,
			data: banks.map((bank, i) => ({
				id: bank.name, // just the code
				text: bank.name,
			})),
		}
	}

	get labels(): ButtonLabel {
		return {
			normal: 'Update Station',
			loading: 'updating...',
			done: 'Updated',
		}
	}

	get type_options(): Select2Options {
		return {
			placeholder: 'Select Station Type',
			minimumResultsForSearch: Infinity,
			//@ts-ignore
			ajax: null,
		}
	}
	get cluster_options(): Select2Options {
		return {
			placeholder: 'Select Cluster',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('cluster.ajax.select.cluster'),
			},

			//@ts-ignore
			templateResult: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, name, text }) => (id ? $(`<span>${name}</span>`) : text),
			
		}
	}
	get manager_options(): Select2Options {
		return {
			
			placeholder: 'Select Manager',
			minimumResultsForSearch: Infinity,
			ajax: {
				method: 'get',
				url: this.route('admin.ajax.select.admin'),
			},

			//@ts-ignore
			templateResult: ({ id, fullname,email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
			//@ts-ignore
			templateSelection: ({ id, fullname,email, text }) => (id ? $(`<span>${fullname} &nbsp; (${email})</span>`) : text),
		}
	}
/*	get state_options(): Select2Options {
		const nigeria = locations.data.find(loc => loc.country === 'Nigeria')
		return {
			ajax: null,
			placeholder: 'Select State',
			minimumResultsForSearch: Infinity,
			data: nigeria
			? nigeria.states.map((state, index) => ({
				id: state, // or use `state` directly if needed
				text: state
				}))
			: [],
		}
	}
*/

	showToast(message: string, type: 'success' | 'error' ) {
			Swal.fire({
				toast: true,
				position: 'top-end', 
				icon: type, 
				title: message,
				showConfirmButton: false, 
				timer: 5000, 
				timerProgressBar: true,
				didOpen: (toast) => {
					// Pause the timer when the mouse hovers over the toast
					toast.addEventListener('mouseenter', Swal.stopTimer)
					// Resume the timer when the mouse leaves
					toast.addEventListener('mouseleave', Swal.resumeTimer)
				}
			})
		}


	async confirmAction() {
		const result = await Swal.fire({
			title: 'Cancel Process',
			text: 'Are you sure you want to cancel this operation?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, Cancel',
			cancelButtonText: 'No, Continue',
			confirmButtonColor: '#dc2626',
		})

		if (!result.isConfirmed) return

		setTimeout(() => {
			this.loading = false
			this.router.visit(this.route('station.page.group'))
		}, 300)
	}
	get checkInput(): boolean {
	return [this.title, this.address, this.type]
		.some(field => field.trim() === '' )
	}
	mounted() {
		if (this.type === 'swap') {
			this.$nextTick()
			this.account.bank = '9Japay Microfinance Bank'
		}
	}
	@Watch('type')
		onTypeChanged(value: string) {
			if (value === 'swap' && this.account.bank !== '9Japay Microfinance Bank'){
				this.account.bank = '9Japay Microfinance Bank';
				
				// Wait for Vue to update the DOM
				this.$nextTick(() => {
						setTimeout(() => {
							this.updateBankSelect()
						}, 200) // allows DOM + Select2 initialization to complete
					})
			}
			
		}
	private updateBankSelect() {
        this.$nextTick(() => {
            const selectElement = this.bankField?.$el?.querySelector('select');
            if (selectElement) {
                $(selectElement).val(this.account.bank).trigger('change');
            }
        });
    }
	@Watch('account.bank')
//	@Watch('account.number')
	onFetchAccountName(value: string) {
	if (typeof value === 'undefined') {
		this.account.index = ''
	
		return
	}

	if (value.length < 10) {
		this.account.index = ''
		
		return
	}

	this.message = ''

	if (value) {
		const selectedBank = banks.find(b => b.name === value);
		if (selectedBank) {
		this.account.index = selectedBank.index;
	
		}
	} 
	else {
		this.account.index = '';
		
		}
	}

	isValidAccountNumber(accountNumber: string): boolean {
		return /^\d{10}$/.test(accountNumber); // Checks if it is 10 digit 
	}

	async validateAccountNumber(accountNumber: string, index : string): Promise<boolean> {
		if (!this.isValidAccountNumber(accountNumber)) {
			this.invalid_acctno_msg = 'Account number must be 10 digits'
			push.error({ title: 'Invalid Account', message: 'Account number must be 10 digits' });
			return false;
		}

		try {
			const response = await this.$http.post(this.route('account.ajax.check'), {
				number: accountNumber,
				index: index
			});

			if (response) {
				console.log(response)
				 // if needed
				return true;
			} else {
				this.invalid_acctno_msg = 'Invalid account number';
				push.error({ title: 'Validation Failed', message: 'Account could not be verified.' });
				return false;
			}
		} catch (error) {
			push.error({ title: 'Validation Failed', message: 'Could not verify account. Please try again.' });
			return false;
		}
	}
	async handleAccountBlur() {
		

		// Skip if bank or account number is missing
		if (!this.account.number || !this.account.index) return;

		this.validatingAccount = true;
		// Perform validation
		const isValid = await this.validateAccountNumber(this.account.number, this.account.index);

		if (!isValid) {
			this.invalid_acctno_msg = 'Invalid account number. Please try again!';
			this.validatingAccount = false;
			this.account.name = '';
			return;
		}

		// Optionally fetch account name from API if valid
		try {
			const response = await this.$http.post(this.route('account.ajax.check'), {
			number:this.account.number,
			index:this.account.index,
			});

			if (response?.data?.data?.name) {
			this.account.name = response.data.data.name;
			this.invalid_acctno_msg = '';
			} else {
			this.account.name = '';
			this.invalid_acctno_msg = 'Could not fetch account name';
			}
		} catch (error) {
			this.account.name = '';
			this.invalid_acctno_msg = 'Error validating account';
			push.error({ title: 'Validation Failed', message: 'Could not verify account. Please try again.' });
			}finally{
				this.validatingAccount = false;
			}

		}
		created() {
			this.title = this.station.title;
			this.address = this.station.address;
			this.type = this.station.type;
			//this.account.bank ="9Japay Microfinance Bank";
			
			
			this.account.index = this.station.account?.index;
			this.account.number = this.station.account?.number;
			this.onTypeChanged(this.type)
	
			
		}
		onSubmit(payload) {
			this.loading = true
			this.message = ''
			payload.index = this.account.index
			this.$http
			.put(this.route('station.ajax.update', { station: this.station.slug }), payload)
				.then((response) => {
					this.done = true
					this.message = response.data.message || 'Station created successfully!'
					Swal.fire({
							title: 'Success!',
							text: this.message,
							icon: 'success',
							confirmButtonText: 'OK',
						}).then(() => {
							this.router.visit(this.route('station.page.group'))
							this.message = ''
							Swal.close()
							
						})
					
					
				})
				.catch((e) => {
			
					if (e.response && e.response.data && e.response.data.errors) {
						const errorMsg = e.response.data.errors.title 
							? e.response.data.errors.title[0] 
							: (e.response.data.message || 'An error occurred.');
							
						this.showToast(errorMsg, 'error');
					} 
				
					else if (e.message) {
						this.showToast(e.message, 'error');
					} 
					else {
						this.showToast('Something went wrong. Please try again.', 'error');
					}
				})
				.finally(() => (this.loading = false))
		}
}

export default toNative(Update)
</script>

<style scoped lang="scss">
.update-layout {
	max-width: 760px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

// ─── Hero ──────────────────────────────────────────────────────────────────
.update-hero {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	background: #fff;
	border-radius: 12px;
	padding: 1.5rem 1.75rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	flex-wrap: wrap;
}

.update-avatar {
	flex-shrink: 0;
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: linear-gradient(135deg, #1e3a2f, #3a7d52);
	color: #fff;
	font-size: 1.35rem;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 12px rgba(30, 58, 47, 0.25);
}

.update-hero-info {
	flex: 1;
	min-width: 0;
}

.update-name {
	font-size: 1.15rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.3rem;
	line-height: 1.2;
}

.update-role-badge {
	display: inline-block;
	padding: 0.2rem 0.7rem;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	background: #dcfce7;
	color: #15803d;
}

.update-hero-actions {
	flex-shrink: 0;
	margin-left: auto;
}

// ─── Form card ─────────────────────────────────────────────────────────────
.update-card {
	background: #fff;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	overflow: hidden;
	transition: box-shadow 0.2s;

	&:hover { box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08); }
}

.update-card-header {
	padding: 0.875rem 1.75rem;
	border-bottom: 1px solid #f1f3f5;
	font-size: 0.85rem;
	font-weight: 600;
	color: #374151;
	background: linear-gradient(to right, #f9fafb, #fff);
	border-left: 3px solid #16a34a;
	display: flex;
	align-items: center;
}

.update-section-divider {
	display: flex;
	align-items: center;
	padding: 0.75rem 1.75rem;
	font-size: 0.8rem;
	font-weight: 600;
	color: #374151;
	background: linear-gradient(to right, #f9fafb, #fff);
	border-top: 1px solid #f1f3f5;
	border-bottom: 1px solid #f1f3f5;
	border-left: 3px solid #3b82f6;
}

:deep(.form-control),
:deep(.form-select) {
	border-color: #e5e7eb;
	border-radius: 8px;
	font-size: 0.88rem;
	transition: border-color 0.15s, box-shadow 0.15s;

	&:focus {
		border-color: #16a34a;
		box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
	}
}

.update-form-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 1.25rem;
	padding: 1.5rem 1.75rem;

	@media (max-width: 560px) {
		grid-template-columns: 1fr;
	}
}

.update-field {
	display: flex;
	flex-direction: column;
	gap: 0.35rem;

	&--full {
		grid-column: 1 / -1;
	}
}

.update-label {
	font-size: 0.78rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #6b7280;
	margin-bottom: 0;
}

.update-hint {
	font-size: 0.8rem;
	margin-top: 0.25rem;

	&--muted  { color: #6b7280; }
	&--error  { color: #dc2626; }
	&--success { color: #16a34a; }
}

// ─── Actions ───────────────────────────────────────────────────────────────
.update-form-actions {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 0.625rem;
	padding: 1rem 1.75rem;
	border-top: 1px solid #f1f3f5;
	background: #f9fafb;
}
</style>
